import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  Alert,
  Tooltip,
  Chip,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { qbusApi } from '../../services/apiClient';
import { usePermissions } from '../../contexts/PermissionContext';
import { useThemeSettings } from '../../contexts/ThemeContext';
import OutputTypeTable from './components/OutputTypeTable';
import OutputTypeDialog from './components/OutputTypeDialog';
import IconMappingPanel from './components/IconMappingPanel';

const QbusOutputTypeManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  const { t } = useThemeSettings();

  const [tab, setTab] = useState(0);
  const [outputTypes, setOutputTypes] = useState([]);
  const [iconMappings, setIconMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [iconLoading, setIconLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);

  const canManage = hasPermission('admin') || hasPermission('qbus.admin');

  const fetchOutputTypes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await qbusApi.getOutputTypes();
      if (response.data.outputTypes) {
        setOutputTypes(response.data.outputTypes);
      }
    } catch (error) {
      console.error('Error fetching output types:', error);
      enqueueSnackbar(t ? t('QbusOutputTypes.fetch_error') : 'Failed to load output types', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, t]);

  const fetchIconMappings = useCallback(async () => {
    setIconLoading(true);
    try {
      const response = await qbusApi.getIconMappings();
      if (response.data.iconMappings) {
        setIconMappings(response.data.iconMappings);
      }
    } catch (error) {
      console.error('Error fetching icon mappings:', error);
      enqueueSnackbar(t ? t('QbusOutputTypes.fetch_icons_error') : 'Failed to load icon mappings', { variant: 'error' });
    } finally {
      setIconLoading(false);
    }
  }, [enqueueSnackbar, t]);

  useEffect(() => {
    fetchOutputTypes();
    fetchIconMappings();
  }, [fetchOutputTypes, fetchIconMappings]);

  const handleEdit = (outputType) => {
    setEditingType(outputType);
    setDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingType(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingType(null);
  };

  const handleSave = async (form) => {
    if (!form.name || !form.friendlyName) {
      enqueueSnackbar(t ? t('QbusOutputTypes.validation_required') : 'Name and friendly name are required', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      enqueueSnackbar(
        t ? t('QbusOutputTypes.save_not_implemented') : 'Output type management is read-only. Types are configured in the database.',
        { variant: 'info' }
      );
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving output type:', error);
      enqueueSnackbar(t ? t('QbusOutputTypes.save_error') : 'Failed to save output type', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddIcon = async (data) => {
    setSaving(true);
    try {
      await qbusApi.createIconMapping(data);
      enqueueSnackbar(t ? t('QbusOutputTypes.icon_added') : 'Icon mapping added', { variant: 'success' });
      fetchIconMappings();
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleEditIcon = async (data) => {
    setSaving(true);
    try {
      await qbusApi.updateIconMapping(data.id, { key: data.key, label: data.label });
      enqueueSnackbar(t ? t('QbusOutputTypes.icon_updated') : 'Icon mapping updated', { variant: 'success' });
      fetchIconMappings();
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteIcon = async (id) => {
    setSaving(true);
    try {
      await qbusApi.deleteIconMapping(id);
      enqueueSnackbar(t ? t('QbusOutputTypes.icon_deleted') : 'Icon mapping deleted', { variant: 'success' });
      fetchIconMappings();
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h4">
              {t ? t('QbusOutputTypes.title') : 'Qbus Output Types'}
            </Typography>
            {!loading && outputTypes.length > 0 && (
              <Chip label={outputTypes.length} size="small" color="primary" variant="outlined" />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t ? t('QbusOutputTypes.subtitle') : 'Manage output type definitions for Qbus domotics controllers'}
          </Typography>
        </Box>
        {tab === 0 && (
          <Box>
            <Tooltip title={t ? t('Common.refresh') : 'Refresh'}>
              <IconButton onClick={fetchOutputTypes} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {canManage && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAdd}
                sx={{ ml: 1 }}
              >
                {t ? t('QbusOutputTypes.add') : 'Add Output Type'}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={t ? t('QbusOutputTypes.tab_output_types') : 'Output Types'} />
        <Tab label={t ? t('QbusOutputTypes.tab_icon_mappings') : 'Icon Mappings'} />
      </Tabs>

      {/* Tab 0: Output Types */}
      {tab === 0 && (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            {t ? t('QbusOutputTypes.locked_info') :
              'Output types marked as "Locked" are security-sensitive (doors, gates, alarms) and are only visible/controllable by administrators.'}
          </Alert>

          <OutputTypeTable
            outputTypes={outputTypes}
            loading={loading}
            canManage={canManage}
            onEdit={handleEdit}
            t={t}
          />

          <OutputTypeDialog
            open={dialogOpen}
            onClose={handleCloseDialog}
            outputType={editingType}
            onSave={handleSave}
            saving={saving}
            iconMappings={iconMappings}
            t={t}
          />
        </>
      )}

      {/* Tab 1: Icon Mappings */}
      {tab === 1 && (
        <IconMappingPanel
          iconMappings={iconMappings}
          loading={iconLoading}
          canManage={canManage}
          onRefresh={fetchIconMappings}
          onAdd={handleAddIcon}
          onEdit={handleEditIcon}
          onDelete={handleDeleteIcon}
          saving={saving}
          t={t}
        />
      )}
    </Container>
  );
};

export default QbusOutputTypeManagement;
